/* eslint space-before-function-paren: 0, no-unused-vars: 0, padded-blocks: 0,
   no-trailing-spaces: 0, keyword-spacing: 0 */

import _ from 'lodash'

// If this is set, logger.info will be called instead of console.log
let logger = null

// noinspection JSUnusedGlobalSymbols
export function setLogger(it) {
  logger = it
}

// Note: inefficient with arrays, we're O(m*n) where m is the number of
// changes and n is the length of the array.  We could improve this to O(n)
// if we needed to.

class UnimplementedError extends Error {
  constructor (message) {
    super()
    this.message = message
    this.name = 'Unimplemented Error'
  }
}

class AssertionError extends Error {
  constructor (message) {
    super()
    this.message = message
    this.name = 'Assertion Error'
  }
}

class FieldChange {

  // **NOTE**: 'typeTagToPrototype' is initialized below, at the end of
  // all the FieldChange subclass definitions.  You'll want to add an entry
  // to it if you add any more subclasses of this class (it's used to map
  // the JSON type tag to a prototype)

  static fromJSON(json) {
    return FieldChange.typeTagToPrototype[json.changeType].fromJSON(json)
  }

  constructor(fieldName, changeType) {
    this.fieldName = fieldName
    this.changeType = changeType
  }

  // noinspection JSUnusedGlobalSymbols
  apply(left) {
    throw new UnimplementedError() // abstract
  }

  isIdChange (left) {
    return false
  }

  childDiff () {
    return null
  }

  // noinspection JSUnusedGlobalSymbols
  oldValue (left) {
    return null
  }
}

class FieldAdded extends FieldChange {
  constructor(fieldName, value) {
    super(fieldName, 'FIELD_ADDED')
    this.value = value
  }

  apply(left) {
    if (left) {
      left[this.fieldName] = this.value
    }
  }

  static fromJSON(json) {
    return Object.setPrototypeOf(json, FieldAdded.prototype)
  }
}

class FieldReplaced extends FieldChange {
  constructor (fieldName, value) {
    super(fieldName, 'FIELD_REPLACED')
    this.value = value
  }

  apply (left) {
    if (left && left.hasOwnProperty(this.fieldName)) {
      left[this.fieldName] = this.value
    }
  }

  static fromJSON (json) {
    return Object.setPrototypeOf(json, FieldReplaced.prototype)
  }
}

class FieldRemoved extends FieldChange {
  constructor(fieldName) {
    super(fieldName, 'FIELD_REMOVED')
  }

  apply(left) {
    delete left[this.fieldName]
  }

  static fromJSON(json) {
    return Object.setPrototypeOf(json, FieldRemoved.prototype)
  }

  oldValue (left) {
    return left[this.fieldName]
  }
}

class SimpleFieldChanged extends FieldChange {
  constructor(fieldName, newValue) {
    super(fieldName, 'SIMPLE_FIELD_CHANGED')
    this.newValue = newValue
  }

  apply(left) {
    if (left) {
      left[this.fieldName] = this.newValue
    }
  }

  static fromJSON(json) {
    return Object.setPrototypeOf(json, SimpleFieldChanged.prototype)
  }

  isIdChange (left) {
    if (left.hasOwnProperty('$identifier')) {
      return this.fieldName === left['$identifier']
    } else {
      return this.fieldName === 'id'
    }
  }

  oldValue (left) {
    return left[this.fieldName]
  }
}

class EntityChanged extends FieldChange {
  constructor(fieldName, diff) {
    super(fieldName, 'ENTITY_CHANGED')
    this.diff = diff
  }

  apply(left) {
    if (left && left.hasOwnProperty(this.fieldName)) {
      left[this.fieldName] = this.diff.applyChanges(left[this.fieldName])
    }
  }

  static fromJSON(json) {
    return new EntityChanged(json.fieldName, Diff.fromJSON(json.diff))
  }

  childDiff () {
    return this.diff
  }

  oldValue (left) {
    return left[this.fieldName]
  }
}

class ArrayElemAdded extends FieldChange {
  constructor(fieldName, elem) {
    super(fieldName, 'ARRAY_ELEM_ADDED')

    let elemCopy = copy(elem)

    if (isObject(elem)) {
      elemCopy['$added'] = true
    }

    this.elem = elemCopy
  }

  apply (left) {
    const elementId = idOf(this.elem)
    const index = isObject(this.elem)
      ? _.findIndex(left[this.fieldName], it => idOf(it) === elementId)
      : left[this.fieldName].indexOf(this.elem)

    if (index > -1) {
      delete this.elem.$added
      left[this.fieldName][index] = this.elem
    } else {
      if (left[this.fieldName] === undefined) {
        left[this.fieldName] = []
      }
      left[this.fieldName].push(this.elem)
    }
  }

  static fromJSON(json) {
    return Object.setPrototypeOf(json, ArrayElemAdded.prototype)
  }
}

class ArrayElemReplaced extends FieldChange {
  constructor (fieldName, elem) {
    super(fieldName, 'ARRAY_ELEM_REPLACED')
    this.elem = elem
  }

  apply (left) {
    const elementId = idOf(this.elem)
    const index = _.findIndex(left[this.fieldName], it => idOf(it) === elementId)
    if (index > -1) {
      left[this.fieldName][index] = this.elem
    }
  }

  static fromJSON(json) {
    return Object.setPrototypeOf(json, ArrayElemReplaced.prototype)
  }
}

class ArraySimpleElemRemoved extends FieldChange {
  constructor(fieldName, elem) {
    super(fieldName, 'ARRAY_SIMPLE_ELEM_REMOVED')
    this.elem = elem
  }

  apply(left) {
    left[this.fieldName] = left[this.fieldName].filter(it => it !== this.elem)
  }

  static fromJSON(json) {
    return Object.setPrototypeOf(json, ArraySimpleElemRemoved.prototype)
  }

  oldValue (left) {
    return this.elem
  }
}

// Nashorn (the Java JS engine) doesn't have Array.find()
function findFirst (array, predicate) {

  if (!array) {
    return null
  }

  let len = array.length

  for (let i = 0; i < len; i++) {
    if (predicate(array[i])) {
      return array[i]
    }
  }

  return null
}

class ArrayEntityRemoved extends FieldChange {
  constructor (fieldName, entId) {
    super(fieldName, 'ARRAY_ENTITY_REMOVED')
    this.entId = entId
  }

  apply(left, diffSets) {
    if(diffSets) {
      delete diffSets.entElems[this.entId]
    } else {
      _.remove(left[this.fieldName], elem => isObject(elem) && idOf(elem) === this.entId)
    }
  }

  static fromJSON(json) {
    return Object.setPrototypeOf(json, ArrayEntityRemoved.prototype)
  }

  oldValue (left) {
    return findFirst(left[this.fieldName], it => idOf(it) === this.entId)
  }
}

class ArrayEntityChanged extends FieldChange {
  constructor(fieldName, entId, entDiff) {
    super(fieldName, 'ARRAY_ENTITY_CHANGED')
    this.entId = entId
    this.entDiff = entDiff
  }

  apply(left, diffSets) {
    if(diffSets) {
      this.entDiff.applyChanges(diffSets.entElems[this.entId])
    } else if (left[this.fieldName]) {
      left[this.fieldName].forEach((elem) => {
        if (isObject(elem) && idOf(elem) === this.entId) {
          this.entDiff.applyChanges(elem)
        }
      })
    }
  }

  static fromJSON(json) {
    return new ArrayEntityChanged(
      json.fieldName, json.entId, Diff.fromJSON(json.entDiff))
  }

  childDiff () {
    return this.entDiff
  }

  oldValue (left) {
    return findFirst(left[this.fieldName], it => idOf(it) === this.entId)
  }
}

FieldChange.typeTagToPrototype = {
  'FIELD_ADDED': FieldAdded,
  'FIELD_REPLACED': FieldReplaced,
  'FIELD_REMOVED': FieldRemoved,
  'SIMPLE_FIELD_CHANGED': SimpleFieldChanged,
  'ENTITY_CHANGED': EntityChanged,
  'ARRAY_ELEM_ADDED': ArrayElemAdded,
  'ARRAY_ELEM_REPLACED': ArrayElemReplaced,
  'ARRAY_SIMPLE_ELEM_REMOVED': ArraySimpleElemRemoved,
  'ARRAY_ENTITY_REMOVED': ArrayEntityRemoved,
  'ARRAY_ENTITY_CHANGED': ArrayEntityChanged
}

export function diffObjects(left, right) {
  return new Diff(left, right)
}

export function hasChanges(left, right) {
  return new Diff(left, right, true).hasChanges()
}

// noinspection JSUnusedGlobalSymbols
export function jsonToDiff(diffJSON) {
  return Diff.fromJSON(diffJSON)
}

export function copy(it) {
  return JSON.parse(JSON.stringify(it))
}

export class Diff {

  /**
   * @param left - The Base object
   * @param right - The object with the changes
   * @param shortCircuit - If true the Diff algorithm will stop as soon it finds one change.
   *                       This is useful when you don't care about the actual changes
   *                       and you only want to know whether or not it has changes.
   */
  constructor(left, right, shortCircuit = false) {

    assert(isObject(left) && isObject(right),
      "Arguments to 'diffObjects' must both be non-array objects")

    this.changes = []
    this.shortCircuit = shortCircuit

    const findChanges = f => {
      if(!this.shortCircuit || !this.hasChanges()) {
        f.call(this, left, right)
      }
    }

    findChanges(this.findAddedFields)
    findChanges(this.findRemovedFields)
    findChanges(this.findChangedSimpleFields)
    findChanges(this.findChangedAndReplacedEntities)
    findChanges(this.findChangedArrays)
  }

  static fromJSON(json) {
    let changes = json.changes.map(it => FieldChange.fromJSON(it))
    return Object.setPrototypeOf({ changes: changes }, Diff.prototype)
  }

  // noinspection JSUnusedGlobalSymbols
  hasChanges() {
    return Object.keys(this.changes).length > 0
  }

  applyChanges(left) {
    this.changes.forEach((change) => {
      change.apply(left)
    })

    return left
  }

  /**
   * Removes all changes that mutate 'id' properties.
   *
   * Use case: when automatically generating overrides for existing
   * installations, any generated UUIDs will be different and clutter up
   * the diff with meaningless differences.  This removes all those noisy
   * changes.
   */
  removeIdChanges (left) {

    this.changes = this.changes.filter((change) => {

      if (change.isIdChange(left)) {
        return false
      }

      let childDiff = change.childDiff()

      if (childDiff !== null) {

        childDiff.removeIdChanges(change.oldValue(left))

        if (childDiff.changes.length === 0) {
          return false
        }
      }

      return true
    })
  }

  findChangedArrays(left, right) {
    for(let field in left) {
      if(left.hasOwnProperty(field) && right.hasOwnProperty(field) &&
         !shouldIgnore(field) && isArray(left[field]) && isArray(right[field])) {

        if(_.isEqual(left[field], right[field])) {
          continue
        }

        let leftDiffSet = toDiffSets(left[field])
        let rightDiffSet = toDiffSets(right[field])

        this.findArrayRemovalsReplacementsAndChanges(
          left[field], field, leftDiffSet, rightDiffSet)

        if (this.shortCircuit && this.hasChanges()) return

        this.findArrayAdditions(
          right[field], field, leftDiffSet)
      }
    }
  }

  findArrayRemovalsReplacementsAndChanges(array, field, leftDiffSet, rightDiffSet) {

    let leftEnts = leftDiffSet.entElems
    let rightEnts = rightDiffSet.entElems

    for (const elem of array) {

      if(isSimple(elem) && !rightDiffSet.simpleElems[elem]) {

        this.changes.push(new ArraySimpleElemRemoved(field, elem))
        if (this.shortCircuit) return

      } else if(isObject(elem)) {

        let id = idOf(elem)

        if(!rightEnts.hasOwnProperty(id)) {
          this.changes.push(new ArrayEntityRemoved(field, id))
          if (this.shortCircuit) return
        } else if(rightEnts.hasOwnProperty(id) && leftEnts.hasOwnProperty(id)) {

          const rightEnt = rightEnts[id]

          if (rightEnt.$replace) {
            this.changes.push(new ArrayElemReplaced(field, rightEnt))
            if (this.shortCircuit) {
              return
            }
            continue
          }

          if(!_.isEqual(leftEnts[id], rightEnt)) {
            let diff = diffObjects(leftEnts[id], rightEnt)

            if(diff.hasChanges()) {
              this.changes.push(new ArrayEntityChanged(field, id, diff))
              if (this.shortCircuit) return
            }
          }
        }
      }
    }
  }

  findArrayAdditions(array, field, leftDiffSet) {
    for (const elem of array) {
      if (isSimple(elem) && !leftDiffSet.simpleElems[elem]) {
        this.changes.push(new ArrayElemAdded(field, elem))
        if (this.shortCircuit) return
      } else if (isObject(elem)) {
        if (!leftDiffSet.entElems.hasOwnProperty(idOf(elem))) {
          this.changes.push(new ArrayElemAdded(field, elem))
          if (this.shortCircuit) return
        }
      }
    }
  }

  findChangedAndReplacedEntities (left, right) {
    for (let field in left) {
      if (left.hasOwnProperty(field) && right.hasOwnProperty(field) &&
        !shouldIgnore(field) && isObject(left[field]) && isObject(right[field])) {

        if (right[field].$replace) {
          this.changes.push(new FieldReplaced(field, right[field]))
          if (this.shortCircuit) {
            return
          }
          continue
        }

        if (!_.isEqual(left[field], right[field])) {
          let diff = diffObjects(left[field], right[field])

          if (diff.hasChanges()) {
            this.changes.push(new EntityChanged(field, diff))
            if (this.shortCircuit) return
          }
        }
      }
    }
  }

  findChangedSimpleFields (left, right) {
    for (let field in left) {
      if (left.hasOwnProperty(field) &&
          right.hasOwnProperty(field) &&
          !shouldIgnore(field) &&
          (isSimple(left[field]) || isSimple(right[field])) &&
          left[field] !== right[field]) {

        this.changes.push(new SimpleFieldChanged(field, right[field]))
        if (this.shortCircuit) return
      }
    }
  }

  findAddedFields (left, right) {
    for (let field in right) {
      if (!(field in left) && right.hasOwnProperty(field) && !shouldIgnore(field)) {
        this.changes.push(new FieldAdded(field, right[field]))
        if (this.shortCircuit) return
      }
    }
  }

  findRemovedFields (left, right) {
    for (let field in left) {
      if (!(field in right) && left.hasOwnProperty(field) && left[field] !== undefined && !shouldIgnore(field)) {
        this.changes.push(new FieldRemoved(field))
        if (this.shortCircuit) return
      }
    }
  }
}

// region Utility functions

function logInfo(msg) {
  if (typeof logger !== 'undefined' && logger && logger.info) {
    logger.info(msg)
  } else if (typeof console !== 'undefined' && console && console.log) {
    console.log(msg)
  }
}

const SIMPLE_PROP_TYPES = {
  'boolean': true, 'number': true, 'string': true, 'symbol': true
}

function shouldIgnore(fieldName) {
  return fieldName && typeof fieldName === 'string' &&
    fieldName.length > 0 && fieldName[0] === '$'
}

export function idOf (it) {
  if(!isObject(it)) {
    return it
  } else if(it.hasOwnProperty('$identifier')) {
    return it[it['$identifier']]
  } else if(it.hasOwnProperty('id')) {
    return it.id
  } else {
    logInfo('entity without "id" or "$identifier" property ' + JSON.stringify(it, null, 2))

    throw new AssertionError(
      'Entity had neither an "id" property or an "$identifier" property ' +
      '(see console for details)')
  }
}

function isSimple(it) {
  return it === null || it === undefined || SIMPLE_PROP_TYPES[typeof it] === true
}

function isObject(it) {
  return it !== null && it !== undefined && typeof it === 'object' && it.constructor !== Array
}

function isArray(it) {
  return it !== null && it !== undefined && typeof it === 'object' && it.constructor === Array
}

function assert(condition, message) {
  if(!condition) {
    throw new AssertionError(message)
  }
}

/**
 * Converts an array into two objects.
 *
 * The first object will have each 'simple' element of the array as a
 * property whose name is the property and whose value is 'true'.
 *
 * The second object will map the id of each entity in the array to the
 * id.
 *
 * Returns an object with two properties named 'simpleElems' and 'entElems'
 * referencing the above objects.
 *
 * @param array
 */
export function toDiffSets(array) {

  let simpleElems = {}
  let entElems = {}

  array.forEach((elem) => {

    if(isSimple(elem)) {

      simpleElems[elem] = true

    } else if(isObject(elem)) {

      let id = idOf(elem)

      if(id in entElems) {
        if(!_.isEqual(elem, entElems[id])) {
          logInfo('two conflicting entities in array have the same id, id is ' + id +
              ' and array is ' + JSON.stringify(array))

          throw new AssertionError(
            'two conflicting entities in array have the same id, see console for details')
        } else {
          return
        }
      }

      entElems[id] = elem
    } else if(isArray(elem)) {
      logInfo('array containing an array: ' + JSON.stringify(array))

      throw new AssertionError(
        "object differ can't handle arrays in arrays, see console for details")
    } else {
      logInfo('array element with un-handleable type; element is ' + elem +
        ' and array is ' + JSON.stringify(array))

      throw new AssertionError(
        'object differ unable to handle array element, see console for details')
    }
  })

  return {
    simpleElems: simpleElems,
    entElems: entElems
  }
}

// endregion
